import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";

/**
 * Iter-33: A/B split router for PI landing traffic.
 *
 * Vendors point their ads at `/lp/pi` (short + brand-neutral). We resolve
 * their `?aff` against the backend to see whether A/B is turned on for that
 * vendor. If it is, we roll a coin between /lp/personal-injury and
 * /lp/free-case-review with a STICKY bucket (persisted in localStorage keyed
 * by aff) so the same visitor sees the same variant on refresh — otherwise
 * "which variant converts better?" measurements would be nonsense.
 *
 * If A/B is off (or the aff is unknown), we send them to /lp/personal-injury
 * — the safe default.
 *
 * The full query string (aff, sub, gclid, fbclid, …) is preserved on the
 * redirect so vendor tracking and attribution keep working.
 */
export default function LandingSplit() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("resolving");

  useEffect(() => {
    const aff = params.get("aff") || "";
    const preserved = params.toString();

    (async () => {
      let variants = ["personal-injury"];
      let enabled = false;
      if (aff) {
        try {
          const { data } = await api.get(
            `/public/landing/ab-config?aff=${encodeURIComponent(aff)}`,
          );
          enabled = !!data.ab_split_enabled;
          if (Array.isArray(data.variants) && data.variants.length) {
            variants = data.variants;
          }
        } catch {
          /* unknown / paused / offline — fall back to default */
        }
      }

      const bucketKey = `lp_ab_variant_${aff || "direct"}`;
      let variant = null;
      if (enabled) {
        try { variant = localStorage.getItem(bucketKey); } catch { /* SSR / disabled storage */ }
        if (!variant || !variants.includes(variant)) {
          variant = variants[Math.floor(Math.random() * variants.length)];
          try { localStorage.setItem(bucketKey, variant); } catch { /* ignore */ }
        }
      } else {
        variant = variants[0];
      }

      setStatus("redirecting");
      nav(
        `/lp/${variant}${preserved ? `?${preserved}` : ""}`,
        { replace: true },
      );
    })();
  }, [params, nav]);

  return (
    <div
      data-testid="lp-split-router"
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Inter', system-ui, sans-serif",
        color: "#64748b",
        fontSize: "13px",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
    >
      {status === "resolving" ? "Preparing your case review…" : "Redirecting…"}
    </div>
  );
}
